import { useCallback, useMemo, useState } from 'react'

import { useTripLocationTrail } from '@/feature/replay'

import { useSingleAsset, useSingleVehicle } from '@/lib/features/Assets/api'

import { TaskDefinition } from '../../types/task'
import { findTaskSchedule } from '../../utils/taskSchedule'
import {
  getReplayRange,
  getReplayTrailMessage,
  getReplayUnavailableReason,
  getTrailStatus,
} from './TaskFocusModal.utils'

// Coarse by default: a task's extent can span days, and sampling every minute over that produces
// far more points than the timeline can usefully draw. The slider goes finer on demand.
const DEFAULT_STEP_SIZE_MINUTES = 15

/**
 * Replay state for one task, held locally.
 *
 * Deliberately not the observatory's persistent store: that serialises into the URL's `page-state`
 * query param, so driving replay through it from a modal would rewrite the Execution Board's URL
 * and leave replay state stranded on the observatory afterwards.
 *
 * The task only carries a vehicle id, and the replay components need a full Asset. Vehicle and
 * Asset are separate records with their ids crossed over (a vehicle's `objectId` IS the asset id),
 * so this resolves one to the other — two small requests rather than the whole-fleet fetch the
 * observatory's equivalent marker relies on. Both are lazy until replay is actually opened.
 *
 * The movement history is the task's own trip trail (BRI-1588), not the asset's rolling
 * location-replay: the trail is kept in durable storage keyed by the task, so it reaches back to
 * tasks older than the few days location-replay covers. The task definition id is the trip id.
 *
 * The trail is fetched as soon as the modal opens rather than when Replay is pressed, so the button
 * can say up front whether there is anything to replay. It also means replay opens instantly.
 */
export const useTaskReplay = (task: TaskDefinition) => {
  const [isReplayActive, setIsReplayActive] = useState(false)

  const { data: vehicle } = useSingleVehicle(
    task.assignedVehicle ? { id: task.assignedVehicle.id } : null,
  )
  const { data: asset } = useSingleAsset(
    isReplayActive && vehicle?.objectId != null ? { id: vehicle.objectId } : null,
  )

  // The trip's own extent, so the history lines up with the route already on the map (BRI-1476).
  const schedule = useMemo(() => findTaskSchedule(task), [task])
  const { rangeStartTime, rangeEndTime } = useMemo(() => getReplayRange(schedule), [schedule])

  const [selectionStartTime, setSelectionStartTime] = useState(rangeStartTime)
  const [selectionEndTime, setSelectionEndTime] = useState(rangeEndTime)
  const [stepSize, setStepSize] = useState(DEFAULT_STEP_SIZE_MINUTES)

  const canReplay = task.assignedVehicle != null
  // The scheduled start and end as-is, not the replay range: that is clamped to now for display,
  // while this describes the trip itself. No schedule, no trip to ask telemetry about.
  const trailQuery = useMemo(() => {
    if (!canReplay || !schedule) return null
    return {
      tripId: task.taskDefinition.id,
      startInstant: schedule.start,
      endInstant: schedule.end,
    }
  }, [canReplay, schedule, task.taskDefinition.id])
  const trail = useTripLocationTrail(trailQuery, `${stepSize}m`)
  const trailStatus = getTrailStatus({
    isLoading: trail.isLoading,
    isRefreshing: trail.isRefreshing,
    error: trail.error,
    pointCount: trail.data.length,
  })

  const replayUnavailableReason = getReplayUnavailableReason(trailStatus, task, schedule)

  const startReplay = useCallback(() => {
    if (replayUnavailableReason == null) setIsReplayActive(true)
  }, [replayUnavailableReason])
  const endReplay = useCallback(() => setIsReplayActive(false), [])

  return {
    // The asset resolves a tick after the button is pressed, so both are needed to know whether
    // there is anything to draw yet.
    isReplayActive,
    replayAsset: isReplayActive ? asset : undefined,
    replayHistory: trail.data,
    // Shown on the Replay button before it is pressed, and over the map once replay is open.
    replayUnavailableReason,
    replayTrailMessage: isReplayActive ? getReplayTrailMessage(trailStatus) : null,
    canReplay,
    startReplay,
    endReplay,
    rangeStartTime,
    rangeEndTime,
    selectionStartTime,
    selectionEndTime,
    setSelectionStartTime,
    setSelectionEndTime,
    stepSize,
    setStepSize,
  }
}
